import { ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { safeStorage } from '../utils/safeStorage';

export type ThemePreference = 'system' | 'light' | 'dark';
type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'sera:theme';

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function loadPreference(): ThemePreference {
  const stored = safeStorage.getItem(STORAGE_KEY);
  if (stored === 'system' || stored === 'light' || stored === 'dark')
    return stored;
  return 'light'; // default: light, not OS
}

function applyThemeClass(resolved: ResolvedTheme): void {
  // T-nom-02: toggles html.dark class — no user-controlled data injected into DOM
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

export const useThemeStore = defineStore('theme', () => {
  const preference = ref<ThemePreference>(loadPreference());

  const resolvedTheme = computed<ResolvedTheme>(() =>
    preference.value === 'system' ? getSystemTheme() : preference.value,
  );

  // Apply on init (before first render if called early enough)
  applyThemeClass(resolvedTheme.value);

  watch(resolvedTheme, (t) => applyThemeClass(t));

  watch(preference, (p) => safeStorage.setItem(STORAGE_KEY, p));

  // Re-apply when OS preference changes and user has 'system' selected
  if (typeof window !== 'undefined') {
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', () => {
        if (preference.value === 'system') applyThemeClass(resolvedTheme.value);
      });
  }

  function setPreference(p: ThemePreference): void {
    preference.value = p;
  }

  // Keep backward-compat for DevTenantSwitcher
  const isDark = computed(() => resolvedTheme.value === 'dark');

  function toggleTheme(): void {
    preference.value = isDark.value ? 'light' : 'dark';
  }

  // Deprecated — kept for callsite compatibility
  function setTheme(t: ResolvedTheme): void {
    preference.value = t;
  }

  return {
    preference,
    resolvedTheme,
    isDark,
    setPreference,
    toggleTheme,
    setTheme,
  };
});
